import { useContext, useState } from 'react';
import OTPInput from 'react-otp-input';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { redirecturlQueryParam } from '../app/constants/constants';
import { useAppDispatch, useAppSelector } from '../app/store/hooks';
import { setAuthError } from '../components/authentication/auth.slice';
import { authCall, phoneNumberSubmit } from '../components/authentication/auth.thunk';
import NeedHelpButton from '../components/batsignal/NeedHelpButton';
import useIntervalHandler from '../hooks/useIntervalHandler';
import { ErrorManagementContext } from '../utils/errorManagement/ErrorManagementProvider';
import { BlueBackButton } from '../utils/iconRefs/authIcons';
import { logEvents } from '../utils/logging';
import styles from './OtpPage.module.css';

const secondsToTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
}

const useCountdownTimerString = (timerDuration: number) => {
  const [startTime, setStartTime] = useState<number>(0);
  const [countdownTimerString, setCountdownTimerString] = useState<string | null>(null);

  const { startInterval, removeInterval } = useIntervalHandler(() => {
    const currentTime = Date.now()
    const timeDifference = timerDuration - Math.floor((currentTime - startTime) / 1000)
    if (timeDifference > 0) {
      setCountdownTimerString(secondsToTime(timeDifference))
    } else {
      setCountdownTimerString(null)
      removeInterval()
    }
  });

  const startTimer = () => {
    setStartTime(Date.now());
    startInterval(1000);
  }

  return { countdownTimerString, startTimer };
}

const OtpPage = () => {

  const dispatch = useAppDispatch();

  const history = useHistory();

  const { countdownTimerString, startTimer } = useCountdownTimerString(30);

  const { cookClubLog } = useContext(ErrorManagementContext);

  const [otp, setOtp] = useState('');

  const authError = useAppSelector(state => state.auth.authError);
  const phone = useAppSelector((state) => state.auth.otpLogin.phone);
  const redirecturl = useAppSelector(state => state.auth.redirectParam);
  const isAuthLoading = useAppSelector(state => state.auth.isAuthLoading);
  const isPhoneNumberSubmitLoading = useAppSelector(state => state.auth.loaders.isPhoneNumberSubmitLoading);

  const verifyOtp = () => {
    if (!!phone && otp.length === 6) {
      dispatch(authCall({ history, redirectToPath: redirecturl, otp, phone, cookClubLog }));
    }
  }

  const handleResend = async () => {
    logEvents('null', 'APP_OTP_LOGIN_RETRY', {
      userId: null,
    })
    if (phone) {
      await dispatch(phoneNumberSubmit({ phoneNumber: phone, cookClubLog }))
      startTimer();
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.top_container} >
        <div className={styles.edit_btn_container}>
          <BlueBackButton />
          <Typography
            className={styles.edit_text}
            onClick={() => {
              history.push(`/sign-in?${redirecturlQueryParam}=${redirecturl}`)
              dispatch(setAuthError(null))
            }}
            variant='caption3Light'  >
            Edit
          </Typography>
        </div>
        <div>
          <NeedHelpButton />
        </div>
      </div>
      <div className={styles.text_container}>
        <Typography variant='h6'>Enter the OTP sent to</Typography>
        <Typography variant='h6'>{phone}</Typography>
      </div>
      <div className={styles.input_action_container}>
        <div className={styles.otp_input_box}>
          <OTPInput
            inputType='number'
            numInputs={6}
            onChange={setOtp}
            renderInput={(props) => <input
              {...props}
              className={styles.otp_input}
              style={{ border: authError ? '1px solid #FF0000' : '1px solid #C8C7CC' }}
              type="number" />}
            renderSeparator={<div className={styles.inputBoxSeparator} />}
            value={otp}
          />
          {!!authError && <div className={styles.auth_error_box}><Typography className={styles.auth_error_text} variant='caption4Light'>* Invalid OTP</Typography></div>}
        </div>
        <Button className={otp.length === 6 ? styles.submit_button : styles.disabled_submit_button} onClick={verifyOtp}>
          <Typography variant='caption3Light'>Submit</Typography>
          {isAuthLoading && <div className={styles.auth_loading}><CircularProgress className={styles.circular_progress} size={17} /></div>}
        </Button>
        <div className={styles.resend_text_container}>
          <Typography variant='caption3Light'>Didn&apos;t receive it?</Typography>
          {countdownTimerString ?
            <Typography className={styles.resend_timer} variant='caption3Bold'>Resend in {countdownTimerString}</Typography> :
            <Typography className={styles.resend_btn} onClick={() => handleResend()} variant='caption3Bold'>Resend</Typography>}
          {isPhoneNumberSubmitLoading && <CircularProgress size={15} />}
        </div>
      </div>
    </div>
  )
}

export default OtpPage